import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { puantajQualityApi } from '../api/puantajQualityApi';
import { useAuth } from '../contexts/AuthContext';
import { 
    X, 
    Play, 
    CheckCircle, 
    RotateCcw, 
    AlertTriangle, 
    RefreshCcw, 
    Zap, 
    ShieldCheck, 
    Info,
    Calendar,
    CalendarOff,
    ArrowRight,
    ArrowLeftRight,
    Layers,
    Clock,
    Search,
    CheckSquare,
    Square,
    ChevronLeft,
    ChevronRight,
    Building2
} from 'lucide-react';

const BATCH_ACTIONS = {
    IGNORE_DUPLICATE_ENTRY: 0,
    ADD_EXIT_BEFORE_DUPLICATE_ENTRY: 1,
    IGNORE_DUPLICATE_EXIT: 2,
    ADD_ENTRY_AFTER_DUPLICATE_EXIT: 3,
    ADD_MISSING_ENTRY: 4,
    ADD_MISSING_EXIT: 5,
    CANCEL_LEAVE_AND_COUNT_WORK: 6,
    IGNORE_PASSES_ON_LEAVE_DAY: 7,
    FLIP_DUPLICATE_ENTRY_TO_EXIT: 8,
    FLIP_DUPLICATE_EXIT_TO_ENTRY: 9
};

const ACTION_LABELS = {
    0: 'Ardışık Giriş: Fazla Girişi Yok Say',
    1: 'Ardışık Giriş: Araya Çıkış Ekle',
    2: 'Ardışık Çıkış: Fazla Çıkışı Yok Say',
    3: 'Ardışık Çıkış: Araya Giriş Ekle',
    4: 'Eksik Giriş: Başlangıca Giriş Ekle',
    5: 'Eksik Çıkış: Mesai Bitişine Çıkış Ekle',
    6: 'İzinli Günde Geçiş: Günü İzinden Düş / İptal Et ve Mesaiyi Say (1 Gün Bakiye İadesi)',
    7: 'İzinli Günde Geçiş: Geçişleri Yok Say (İzinli Kalsın)',
    8: 'Ardışık Giriş: 2. Girişin Yönünü Çıkışa Çevir (Giriş ⇄ Çıkış)',
    9: 'Ardışık Çıkış: 1. Çıkışın Yönünü Girişe Çevir (Çıkış ⇄ Giriş)'
};

export function BatchCorrectionModal({
    isOpen,
    onClose,
    initialDate,
    initialAction = null,
    onDateChange,
    onApplied
}) {
    const { hasPermission } = useAuth();
    const canCorrect = hasPermission('puantaj_quality:correct');
    
    const [date, setDate] = useState(initialDate);
    const [activeAction, setActiveAction] = useState(
        initialAction !== null && initialAction !== undefined
            ? initialAction
            : BATCH_ACTIONS.CANCEL_LEAVE_AND_COUNT_WORK
    );
    const [daySummary, setDaySummary] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [applying, setApplying] = useState(false);
    const [applyResult, setApplyResult] = useState(null);
    const [undoing, setUndoing] = useState(false);
    const [error, setError] = useState(null);

    // 10k-20k Personel Stres Testi: Seçim, Arama ve Sayfalama State'leri
    const [selectedEmpIds, setSelectedEmpIds] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const fetchDaySummary = useCallback(async (targetDate) => {
        try {
            const s = await puantajQualityApi.getDashboardSummary(targetDate);
            setDaySummary(s);
        } catch (err) {
            console.error('Günlük özet alınamadı:', err);
        }
    }, []);

    const runPreview = useCallback(async (targetDate, actionToRun) => {
        setLoadingPreview(true);
        setError(null);
        setApplyResult(null);
        try {
            const res = await puantajQualityApi.previewBatch({
                date: targetDate,
                action: parseInt(actionToRun, 10)
            });
            setPreviewData(res);
            const allIds = new Set((res?.items || []).map(item => item.employeeId));
            setSelectedEmpIds(allIds);
            setCurrentPage(1);
        } catch (err) {
            setError(err.response?.data?.Message || err.response?.data?.message || err.message || 'Önizleme oluşturulurken hata oluştu.');
        } finally {
            setLoadingPreview(false);
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const startDate = initialDate || date;
        setDate(startDate);
        const startAction = initialAction !== null && initialAction !== undefined
            ? initialAction
            : BATCH_ACTIONS.CANCEL_LEAVE_AND_COUNT_WORK;
        setActiveAction(startAction);
        setPreviewData(null);
        setApplyResult(null);
        setError(null);
        setSearchTerm('');
        setCurrentPage(1);
        fetchDaySummary(startDate);
        runPreview(startDate, startAction);
    }, [isOpen, initialDate, initialAction, fetchDaySummary, runPreview]);

    const handleDateInput = (newDate) => {
        if (!newDate) return;
        setDate(newDate);
        setPreviewData(null);
        setApplyResult(null);
        setSearchTerm('');
        setCurrentPage(1);
        if (onDateChange) onDateChange(newDate);
        fetchDaySummary(newDate);
        runPreview(newDate, activeAction);
    };

    const handleSelectAction = (actionVal) => {
        setActiveAction(actionVal);
        setPreviewData(null);
        setApplyResult(null);
        setSearchTerm('');
        setCurrentPage(1);
        runPreview(date, actionVal);
    };

    const handlePreview = () => {
        runPreview(date, activeAction);
    };

    // Arama filtresi (10k-20k kayıtta useMemo ile anlık çalışır)
    const filteredItems = useMemo(() => {
        if (!previewData?.items) return [];
        const q = searchTerm.trim().toLocaleLowerCase('tr-TR');
        if (!q) return previewData.items;
        return previewData.items.filter(item => {
            const nameMatch = (item.fullName || '').toLocaleLowerCase('tr-TR').includes(q);
            const deptMatch = (item.departmentName || '').toLocaleLowerCase('tr-TR').includes(q);
            const reasonMatch = (item.reasonSummary || '').toLocaleLowerCase('tr-TR').includes(q);
            const idMatch = String(item.employeeId).includes(q);
            return nameMatch || deptMatch || reasonMatch || idMatch;
        });
    }, [previewData, searchTerm]);

    // Sayfalanmış kayıtlar (DOM'a sadece pageSize kadar satır basılır -> 20.000 personelde sıfır donma)
    const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const paginatedItems = useMemo(() => {
        const startIndex = (safeCurrentPage - 1) * pageSize;
        return filteredItems.slice(startIndex, startIndex + pageSize);
    }, [filteredItems, safeCurrentPage, pageSize]);

    const toggleEmployeeSelection = (empId) => {
        setSelectedEmpIds(prev => {
            const next = new Set(prev);
            if (next.has(empId)) {
                next.delete(empId);
            } else {
                next.add(empId);
            }
            return next;
        });
    };

    const areAllFilteredSelected = useMemo(() => {
        if (filteredItems.length === 0) return false;
        return filteredItems.every(item => selectedEmpIds.has(item.employeeId));
    }, [filteredItems, selectedEmpIds]);

    const toggleSelectAllFiltered = () => {
        setSelectedEmpIds(prev => {
            const next = new Set(prev);
            if (areAllFilteredSelected) {
                filteredItems.forEach(item => next.delete(item.employeeId));
            } else {
                filteredItems.forEach(item => next.add(item.employeeId));
            }
            return next;
        });
    };

    const handleApply = async () => {
        if (!canCorrect || !previewData || !previewData.items || previewData.items.length === 0) return;
        const empIds = Array.from(selectedEmpIds);
        if (empIds.length === 0) {
            setError('Lütfen işlem uygulanacak en az bir personel seçin.');
            return;
        }

        setApplying(true);
        setError(null);
        try {
            const res = await puantajQualityApi.applyBatch({
                date,
                action: parseInt(activeAction, 10),
                employeeIds: empIds
            });
            setApplyResult(res);
            setPreviewData(null);
            fetchDaySummary(date);
            if (onApplied) onApplied();
        } catch (err) {
            setError(err.response?.data?.Message || err.response?.data?.message || err.message || 'İşlem uygulanırken hata oluştu.');
        } finally {
            setApplying(false);
        }
    };

    const handleUndo = async () => {
        if (!applyResult || !applyResult.batchLogId) return;
        setUndoing(true);
        setError(null);
        try {
            await puantajQualityApi.undoBatch(applyResult.batchLogId);
            setApplyResult(null);
            fetchDaySummary(date);
            runPreview(date, activeAction);
            if (onApplied) onApplied();
        } catch (err) {
            setError(err.response?.data?.Message || err.response?.data?.message || err.message || 'Geri alma sırasında hata oluştu.');
        } finally {
            setUndoing(false);
        }
    };

    if (!isOpen) return null;

    const renderOpKindBadge = (op) => {
        if (op.kind === 'CancelLeaveAndCountWork') {
            return (
                <span className="px-2 py-0.5 rounded-md font-black uppercase text-[9px] tracking-wider bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 border border-rose-200 dark:border-rose-800/50">
                    Günü İzinden Düş & Bakiye İadesi
                </span>
            );
        }
        if (op.kind === 'FlipPass') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-black uppercase text-[9px] tracking-wider bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800/50">
                    <ArrowLeftRight size={10} /> Yön Değiştir (Giriş ⇄ Çıkış)
                </span>
            );
        }
        if (op.kind === 'IgnorePass') {
            return (
                <span className="px-2 py-0.5 rounded-md font-black uppercase text-[9px] tracking-wider bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50">
                    Geçişi Yok Say
                </span>
            );
        }
        if (op.kind === 'AddPass') {
            return (
                <span className="px-2 py-0.5 rounded-md font-black uppercase text-[9px] tracking-wider bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/50">
                    Yeni Geçiş Ekle
                </span>
            );
        }
        return (
            <span className="px-2 py-0.5 rounded-md font-black uppercase text-[9px] tracking-wider bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50">
                Kaydı Aktifleştir
            </span>
        );
    };

    const renderPreviewTable = () => {
        if (!previewData) return null;
        if (previewData.items.length === 0) {
            return (
                <div className="p-10 text-center text-gray-500 dark:text-gray-400 bg-gray-50/50 dark:bg-gray-900/20 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 m-4">
                    <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 dark:text-emerald-400 mb-3 opacity-80" />
                    <h4 className="font-bold text-gray-800 dark:text-gray-200 text-base mb-1">İşlem Gerektiren Kayıt Bulunamadı</h4>
                    <p className="text-xs max-w-md mx-auto">
                        Seçilen <strong>"{ACTION_LABELS[activeAction]}"</strong> stratejisine uygun düzeltilecek puantaj kaydı bulunmuyor. Sol menüden farklı bir risk kategorisi seçebilirsiniz.
                    </p>
                </div>
            );
        }

        return (
            <div className="flex flex-col flex-1 min-h-0">
                {/* Arama ve Toplu Seçim Araç Çubuğu */}
                <div className="p-3.5 bg-gray-50/90 dark:bg-gray-900/70 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            placeholder="Personel adı, departman, sicil veya hata açıklaması ara..."
                            className="w-full pl-9 pr-8 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X size={13} />
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                        <span className="px-2.5 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-bold border border-indigo-100 dark:border-indigo-800/50">
                            Seçili: {selectedEmpIds.size} / {previewData.items.length} Personel
                        </span>
                        <button
                            type="button"
                            onClick={() => setSelectedEmpIds(new Set(previewData.items.map(x => x.employeeId)))}
                            className="px-2.5 py-1 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 font-bold transition-colors"
                        >
                            Tümünü Seç
                        </button>
                        <button
                            type="button"
                            onClick={() => setSelectedEmpIds(new Set())}
                            className="px-2.5 py-1 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 font-bold transition-colors"
                        >
                            Temizle
                        </button>
                    </div>
                </div>

                {/* Tablo Gövdesi (Sadece aktif sayfadaki 50 kayıt render edilir) */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                        <thead className="bg-gray-100/95 dark:bg-gray-900/95 sticky top-0 z-10 backdrop-blur-sm">
                            <tr>
                                <th className="px-3 py-3 text-left w-10">
                                    <button
                                        type="button"
                                        onClick={toggleSelectAllFiltered}
                                        className="text-indigo-600 dark:text-indigo-400 hover:opacity-80 flex items-center"
                                        title={areAllFilteredSelected ? 'Filtrelenenlerin seçimini kaldır' : 'Filtrelenenlerin tümünü seç'}
                                    >
                                        {areAllFilteredSelected ? <CheckSquare size={17} /> : <Square size={17} className="text-gray-400" />}
                                    </button>
                                </th>
                                <th className="px-3 py-3 text-left text-[11px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider w-52">
                                    Personel / Departman
                                </th>
                                <th className="px-3 py-3 text-left text-[11px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider w-60">
                                    Tespit Edilen Risk (Neden)
                                </th>
                                <th className="px-3 py-3 text-left text-[11px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Uygulanacak Otomatik Düzeltme
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700/50">
                            {paginatedItems.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="p-8 text-center text-xs text-gray-400">
                                        Arama kriterine ("{searchTerm}") uygun personel bulunamadı.
                                    </td>
                                </tr>
                            ) : (
                                paginatedItems.map((item) => {
                                    const isSelected = selectedEmpIds.has(item.employeeId);
                                    return (
                                        <tr
                                            key={item.puantajId || item.employeeId}
                                            onClick={() => toggleEmployeeSelection(item.employeeId)}
                                            className={`transition-colors align-top cursor-pointer ${
                                                isSelected
                                                    ? 'hover:bg-indigo-50/40 dark:hover:bg-indigo-900/15'
                                                    : 'opacity-55 bg-gray-50/50 dark:bg-gray-900/30 hover:opacity-80'
                                            }`}
                                        >
                                            <td className="px-3 py-3.5" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleEmployeeSelection(item.employeeId)}
                                                    className="text-indigo-600 dark:text-indigo-400 mt-0.5"
                                                >
                                                    {isSelected ? <CheckSquare size={17} /> : <Square size={17} className="text-gray-400" />}
                                                </button>
                                            </td>
                                            <td className="px-3 py-3.5">
                                                <div className="font-bold text-gray-900 dark:text-gray-100 text-xs leading-snug">
                                                    {item.fullName}
                                                </div>
                                                <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                                                    <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                                                        #{item.employeeId}
                                                    </span>
                                                    {item.departmentName && (
                                                        <span className="inline-flex items-center gap-1 truncate max-w-[130px]" title={item.departmentName}>
                                                            <Building2 size={10} /> {item.departmentName}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-3.5 text-gray-600 dark:text-gray-300 text-xs">
                                                <div className="leading-relaxed bg-amber-50/70 dark:bg-amber-950/30 border border-amber-200/70 dark:border-amber-800/50 rounded-xl px-2.5 py-2 text-amber-900 dark:text-amber-200 text-[11px]">
                                                    {item.reasonSummary || 'Kalite kuralı ihlali'}
                                                </div>
                                            </td>
                                            <td className="px-3 py-3.5">
                                                <div className="space-y-1.5">
                                                    {item.plannedOps.map((op, i) => (
                                                        <div key={i} className="rounded-xl border border-gray-100 dark:border-gray-700/80 bg-gray-50/70 dark:bg-gray-900/40 px-3 py-2 text-xs space-y-1">
                                                            <div className="flex flex-wrap items-center gap-1.5">
                                                                {renderOpKindBadge(op)}
                                                                {op.title && (
                                                                    <span className="font-bold text-gray-800 dark:text-gray-100 text-[11px]">{op.title}</span>
                                                                )}
                                                                {op.kind !== 'CancelLeaveAndCountWork' && op.time && (
                                                                    <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded text-[10px]">
                                                                        {new Date(op.time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })} · {op.typeLabel}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {op.reason && (
                                                                <div className="text-[11px] text-gray-600 dark:text-gray-300 leading-snug">
                                                                    <span className="font-semibold text-gray-500 dark:text-gray-400">İşlem: </span>
                                                                    {op.reason}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Sayfalama (Pagination) Alt Çubuğu */}
                <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-900/80 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                        <span>
                            Toplam <strong>{filteredItems.length}</strong> kayıttan{' '}
                            <strong>{filteredItems.length === 0 ? 0 : (safeCurrentPage - 1) * pageSize + 1}</strong> -{' '}
                            <strong>{Math.min(safeCurrentPage * pageSize, filteredItems.length)}</strong> arası gösteriliyor
                        </span>
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-xs font-bold text-gray-700 dark:text-gray-300"
                        >
                            <option value={50}>50 / sayfa</option>
                            <option value={100}>100 / sayfa</option>
                            <option value={250}>250 / sayfa</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            disabled={safeCurrentPage <= 1}
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        <span className="px-2.5 py-1 font-bold text-gray-700 dark:text-gray-300">
                            Sayfa {safeCurrentPage} / {totalPages}
                        </span>
                        <button
                            type="button"
                            disabled={safeCurrentPage >= totalPages}
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/75 backdrop-blur-sm p-2 sm:p-4 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden border border-gray-100 dark:border-gray-700">
                
                {/* Modal Başlığı */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex flex-wrap justify-between items-center gap-3 bg-gray-50/70 dark:bg-gray-800/70 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-indigo-600 text-white rounded-2xl shadow-lg shadow-indigo-500/20">
                            <Zap size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-gray-900 dark:text-gray-100 tracking-tight">
                                Puantaj Riskleri & Toplu Kalite Düzeltme Merkezi
                            </h2>
                            <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                                İzinli günde geçiş çakışmalarını, yön hatalarını ve eksik/mükerrer kart okutmalarını yüksek performansla analiz edin ve toplu düzeltin
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Tarih Seçici */}
                        <div className="flex items-center gap-2 bg-white dark:bg-gray-900 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                            <Calendar size={14} className="text-indigo-500" />
                            <span className="text-[10px] font-black uppercase text-gray-400">Tarih:</span>
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => handleDateInput(e.target.value)}
                                className="text-xs font-bold text-gray-800 dark:text-gray-100 bg-transparent focus:outline-none cursor-pointer"
                            />
                        </div>

                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Modal Gövdesi (Tekil bağımsız scroll panelleri) */}
                <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
                    
                    {/* Sol Panel: Risk Kategorileri & Strateji Seçimi */}
                    <div className="w-full lg:w-[370px] shrink-0 border-b lg:border-b-0 lg:border-r border-gray-100 dark:border-gray-700 p-4 overflow-y-auto custom-scrollbar bg-gray-50/40 dark:bg-gray-900/20 space-y-3">
                        
                        <div className="flex items-center justify-between px-1">
                            <span className="text-[11px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest flex items-center gap-1.5">
                                <Layers size={13} className="text-indigo-500" /> Düzeltme Stratejileri
                            </span>
                            {daySummary && (
                                <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
                                    Toplam {daySummary.totalEmployeesWithIssues} Riskli Personel
                                </span>
                            )}
                        </div>

                        {/* 1. İZİNLİ GÜNDE GEÇİŞ KAYDI */}
                        <div className={`p-3.5 rounded-2xl border transition-all ${
                            activeAction === 6 || activeAction === 7
                                ? 'bg-rose-50/60 dark:bg-rose-950/30 border-rose-300 dark:border-rose-800 shadow-sm'
                                : 'bg-white dark:bg-gray-800 border-gray-200/80 dark:border-gray-700'
                        }`}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="font-black text-xs text-rose-700 dark:text-rose-300 uppercase tracking-wider flex items-center gap-1.5">
                                    <CalendarOff size={14} className="text-rose-600 dark:text-rose-400" />
                                    İzinli Günde Geçiş Kaydı
                                </div>
                                <span className={`text-[11px] font-black px-2 py-0.5 rounded-full ${
                                    (daySummary?.leaveConflictCount || 0) > 0
                                        ? 'bg-rose-600 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                }`}>
                                    {daySummary?.leaveConflictCount ?? 0}
                                </span>
                            </div>
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-2.5 leading-relaxed">
                                Personel onaylı izinde görünmesine rağmen o gün kart okutmuş (giriş/çıkış yapmış). Çok günlü izinlerde yalnızca çalışılan gün izinden düşülür.
                            </p>
                            <div className="space-y-1.5">
                                {[
                                    {
                                        id: BATCH_ACTIONS.CANCEL_LEAVE_AND_COUNT_WORK,
                                        label: 'Günü İzinden Düş ve Mesaiyi Say',
                                        desc: 'Tek günlük izni iptal eder; çok günlü izinde ise sadece o günü izinden çıkarıp 1 gün bakiyeyi iade eder ve çalışmasını sayar.'
                                    },
                                    {
                                        id: BATCH_ACTIONS.IGNORE_PASSES_ON_LEAVE_DAY,
                                        label: 'Geçişleri Yok Say (İzinli Kalsın)',
                                        desc: 'O gündeki kart okutmalarını yok sayar, personel izinli kalmaya devam eder.'
                                    }
                                ].map(opt => (
                                    <label
                                        key={opt.id}
                                        onClick={() => handleSelectAction(opt.id)}
                                        className={`flex items-start gap-2.5 p-2.5 rounded-xl cursor-pointer transition-all border ${
                                            activeAction === opt.id
                                                ? 'bg-rose-600 text-white border-rose-600 shadow-md shadow-rose-500/20'
                                                : 'bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700/50'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="batchAction"
                                            value={opt.id}
                                            checked={activeAction === opt.id}
                                            onChange={() => {}}
                                            className="mt-0.5 hidden"
                                        />
                                        <div className={`w-3.5 h-3.5 mt-0.5 rounded-full border-2 flex items-center justify-center shrink-0 ${activeAction === opt.id ? 'border-white' : 'border-gray-400'}`}>
                                            {activeAction === opt.id && <div className="w-1.5 h-1.5 rounded-full bg-white"></div>}
                                        </div>
                                        <div>
                                            <div className="text-xs font-bold leading-tight">{opt.label}</div>
                                            <div className={`text-[10px] mt-0.5 leading-snug ${activeAction === opt.id ? 'text-rose-100' : 'text-gray-400 dark:text-gray-500'}`}>
                                                {opt.desc}
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* 2. EKSİK GİRİŞ / EKSİK ÇIKIŞ */}
                        <div className="bg-white dark:bg-gray-800 p-3.5 rounded-2xl border border-gray-200/80 dark:border-gray-700 space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="font-black text-xs text-red-600 dark:text-red-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Clock size={14} /> Eksik Giriş / Çıkış
                                </div>
                                <span className="text-[11px] font-black px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300">
                                    {(daySummary?.missingEntryCount || 0) + (daySummary?.missingExitCount || 0)}
                                </span>
                            </div>
                            <div className="space-y-1.5">
                                {[
                                    {
                                        id: BATCH_ACTIONS.ADD_MISSING_EXIT,
                                        label: `Eksik Çıkışları Tamamla (${daySummary?.missingExitCount ?? 0})`,
                                        desc: 'Vardiya bitiş saatine otomatik çıkış kaydı ekler.'
                                    },
                                    {
                                        id: BATCH_ACTIONS.ADD_MISSING_ENTRY,
                                        label: `Eksik Girişleri Tamamla (${daySummary?.missingEntryCount ?? 0})`,
                                        desc: 'Vardiya başlangıç saatine otomatik giriş kaydı ekler.'
                                    }
                                ].map(opt => (
                                    <label
                                        key={opt.id}
                                        onClick={() => handleSelectAction(opt.id)}
                                        className={`flex items-start gap-2.5 p-2.5 rounded-xl cursor-pointer transition-all border ${
                                            activeAction === opt.id
                                                ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-500/20'
                                                : 'bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700/50'
                                        }`}
                                    >
                                        <div className={`w-3.5 h-3.5 mt-0.5 rounded-full border-2 flex items-center justify-center shrink-0 ${activeAction === opt.id ? 'border-white' : 'border-gray-400'}`}>
                                            {activeAction === opt.id && <div className="w-1.5 h-1.5 rounded-full bg-white"></div>}
                                        </div>
                                        <div>
                                            <div className="text-xs font-bold leading-tight">{opt.label}</div>
                                            <div className={`text-[10px] mt-0.5 leading-snug ${activeAction === opt.id ? 'text-indigo-100' : 'text-gray-400 dark:text-gray-500'}`}>
                                                {opt.desc}
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* 3. ARDIŞIK GİRİŞ */}
                        <div className="bg-white dark:bg-gray-800 p-3.5 rounded-2xl border border-gray-200/80 dark:border-gray-700 space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="font-black text-xs text-amber-600 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-amber-500"></span> Ardışık Giriş (Giriş → Giriş)
                                </div>
                                <span className="text-[11px] font-black px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                    {daySummary?.consecutiveEntryCount ?? 0}
                                </span>
                            </div>
                            <div className="space-y-1.5">
                                {[
                                    { id: BATCH_ACTIONS.IGNORE_DUPLICATE_ENTRY, label: 'Mükerrer Girişi Yok Say' },
                                    { id: BATCH_ACTIONS.FLIP_DUPLICATE_ENTRY_TO_EXIT, label: '2. Girişin Yönünü Çıkışa Çevir (Giriş ⇄ Çıkış)' },
                                    { id: BATCH_ACTIONS.ADD_EXIT_BEFORE_DUPLICATE_ENTRY, label: 'İki Giriş Arasına Çıkış Ekle (-1 dk)' }
                                ].map(opt => (
                                    <label
                                        key={opt.id}
                                        onClick={() => handleSelectAction(opt.id)}
                                        className={`flex items-center gap-2.5 p-2.5 rounded-xl cursor-pointer transition-all border ${
                                            activeAction === opt.id
                                                ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-500/20'
                                                : 'bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700/50'
                                        }`}
                                    >
                                        <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${activeAction === opt.id ? 'border-white' : 'border-gray-400'}`}>
                                            {activeAction === opt.id && <div className="w-1.5 h-1.5 rounded-full bg-white"></div>}
                                        </div>
                                        <span className="text-xs font-bold">{opt.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* 4. ARDIŞIK ÇIKIŞ */}
                        <div className="bg-white dark:bg-gray-800 p-3.5 rounded-2xl border border-gray-200/80 dark:border-gray-700 space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="font-black text-xs text-amber-600 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-amber-500"></span> Ardışık Çıkış (Çıkış → Çıkış)
                                </div>
                                <span className="text-[11px] font-black px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                    {daySummary?.consecutiveExitCount ?? 0}
                                </span>
                            </div>
                            <div className="space-y-1.5">
                                {[
                                    { id: BATCH_ACTIONS.IGNORE_DUPLICATE_EXIT, label: 'Fazla Çıkışı Yok Say' },
                                    { id: BATCH_ACTIONS.FLIP_DUPLICATE_EXIT_TO_ENTRY, label: '1. Çıkışın Yönünü Girişe Çevir (Çıkış ⇄ Giriş)' },
                                    { id: BATCH_ACTIONS.ADD_ENTRY_AFTER_DUPLICATE_EXIT, label: 'İki Çıkış Arasına Giriş Ekle (+1 dk)' }
                                ].map(opt => (
                                    <label
                                        key={opt.id}
                                        onClick={() => handleSelectAction(opt.id)}
                                        className={`flex items-center gap-2.5 p-2.5 rounded-xl cursor-pointer transition-all border ${
                                            activeAction === opt.id
                                                ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-500/20'
                                                : 'bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700/50'
                                        }`}
                                    >
                                        <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${activeAction === opt.id ? 'border-white' : 'border-gray-400'}`}>
                                            {activeAction === opt.id && <div className="w-1.5 h-1.5 rounded-full bg-white"></div>}
                                        </div>
                                        <span className="text-xs font-bold">{opt.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <button
                            onClick={handlePreview}
                            disabled={loadingPreview || applying}
                            className="w-full flex items-center justify-center gap-2 bg-gray-900 dark:bg-indigo-600 hover:bg-gray-800 dark:hover:bg-indigo-700 text-white font-black uppercase tracking-widest text-xs py-3 px-4 rounded-2xl shadow-md transition-all disabled:opacity-50"
                        >
                            {loadingPreview ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-current" />}
                            Yeniden Analiz Et
                        </button>
                    </div>

                    {/* Sağ Panel: Önizleme Tablosu & Sonuç Ekranı */}
                    <div className="flex-1 min-h-0 flex flex-col bg-white dark:bg-gray-800 overflow-hidden">
                        
                        {/* Seçili Strateji Bilgi Barı */}
                        {previewData && (
                            <div className={`px-5 py-3.5 text-white flex flex-wrap items-center justify-between gap-3 shrink-0 ${
                                activeAction === 6 || activeAction === 7
                                    ? 'bg-gradient-to-r from-rose-600 to-amber-600'
                                    : 'bg-gradient-to-r from-indigo-600 to-blue-600'
                            }`}>
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white/20 rounded-xl backdrop-blur-md">
                                        <Info size={18} className="text-white" />
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Aktif Strateji Önizlemesi</div>
                                        <div className="text-sm font-black tracking-tight">{ACTION_LABELS[activeAction]}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-5 text-right">
                                    <div>
                                        <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Etkilenen Personel</div>
                                        <div className="text-lg font-black">{previewData.affectedEmployeesCount}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Toplam Operasyon</div>
                                        <div className="text-lg font-black">{previewData.totalActionsCount}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Hata ve Başarı Bildirimleri */}
                        {error && (
                            <div className="m-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300 px-4 py-3 rounded-2xl flex items-center gap-3 shrink-0">
                                <AlertTriangle className="w-5 h-5 shrink-0 text-red-500" />
                                <span className="text-xs font-bold">{error}</span>
                            </div>
                        )}

                        {applyResult && (
                            <div className="m-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-900/40 text-emerald-800 dark:text-emerald-200 p-5 rounded-2xl flex flex-col sm:flex-row justify-between items-center gap-4 shrink-0">
                                <div className="flex items-center gap-3.5">
                                    <div className="p-3 bg-emerald-500 text-white rounded-2xl shadow-lg shadow-emerald-500/20">
                                        <ShieldCheck className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h4 className="font-black text-sm uppercase tracking-wide">Toplu Düzeltme Tamamlandı</h4>
                                        <p className="text-xs opacity-90 mt-0.5 font-medium">
                                            <strong>{applyResult.affectedEmployeesCount}</strong> personelde toplam <strong>{applyResult.actionsCount}</strong> düzeltme başarıyla uygulandı ve puantajları yeniden hesaplandı.
                                        </p>
                                    </div>
                                </div>
                                {canCorrect && (
                                    <button
                                        onClick={handleUndo}
                                        disabled={undoing}
                                        className="w-full sm:w-auto flex items-center justify-center gap-2 text-xs bg-white dark:bg-gray-800 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 px-4 py-2.5 rounded-xl hover:bg-emerald-100 dark:hover:bg-gray-700 transition-all font-black uppercase tracking-wider shadow-sm shrink-0"
                                    >
                                        {undoing ? <RefreshCcw className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                                        İşlemi Geri Al (Undo)
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Önizleme İçeriği */}
                        {loadingPreview ? (
                            <div className="flex-1 flex flex-col justify-center items-center p-12">
                                <RefreshCcw className="w-10 h-10 text-indigo-500 animate-spin mb-3" />
                                <span className="text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest text-xs">
                                    Puantaj ve İzin Kayıtları Analiz Ediliyor...
                                </span>
                            </div>
                        ) : !previewData ? (
                            <div className="flex-1 flex flex-col justify-center items-center text-center p-8 m-4 bg-gray-50/50 dark:bg-gray-900/20 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                                <ArrowRight className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
                                <h5 className="text-gray-800 dark:text-gray-200 font-black uppercase tracking-widest text-xs mb-1">
                                    Strateji Seçin
                                </h5>
                                <p className="text-gray-500 dark:text-gray-400 text-xs max-w-sm">
                                    Sol menüden bir düzeltme yöntemi seçtiğinizde etkilenen personeller ve yapılacak işlemler burada listelenir.
                                </p>
                            </div>
                        ) : (
                            renderPreviewTable()
                        )}
                    </div>
                </div>

                {/* Modal Alt Çubuğu (Footer) */}
                <div className="px-6 py-3.5 border-t border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800 flex flex-col sm:flex-row justify-between items-center gap-3 shrink-0">
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400 font-bold">
                        <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                        <span>
                            Onayladığınızda seçili personellerin düzeltmeleri uygulanır ve ilgili hafta puantajı otomatik yeniden hesaplanır.
                        </span>
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <button
                            onClick={onClose}
                            className="flex-1 sm:flex-none px-5 py-2.5 text-xs font-black uppercase tracking-widest text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded-xl transition-all"
                        >
                            Kapat
                        </button>

                        <button
                            onClick={handleApply}
                            disabled={!canCorrect || !previewData || selectedEmpIds.size === 0 || applying}
                            className="flex-1 sm:flex-none px-6 py-2.5 text-xs font-black uppercase tracking-widest text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                        >
                            {applying ? (
                                <>
                                    <RefreshCcw className="w-4 h-4 animate-spin" /> Uygulanıyor...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-4 h-4" />
                                    Seçili {selectedEmpIds.size} Personel İçin Onayla
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BatchCorrectionModal;
